import {
  FAIR_SEO_DEFAULT_DESCRIPTION,
  FAIR_SEO_DEFAULT_TITLE,
  fairAbsoluteUrl,
  fairOgImageUrl,
} from '@/seo/fairSeo.defaults'

export interface FairRouteSeo {
  title: string
  description: string
  path: string
  ogImage?: string
  /** When true, inject Organization + WebSite JSON-LD */
  jsonLdWebsite?: boolean
  /** e.g. `index,follow` / `noindex,follow`. Default applied in useFairSeo when omitted. */
  robots?: string
}

export {
  buildFairSearchSeo,
  buildFairSearchDescription,
  buildFairSearchTitle,
  fairSearchCanonicalPath,
  isFairSearchSeoIndexable,
  FAIR_SEO_ROBOTS_INDEX,
  FAIR_SEO_ROBOTS_NOINDEX,
} from '@/seo/fairSearchSeo'

const HOME_SEO: FairRouteSeo = {
  title: FAIR_SEO_DEFAULT_TITLE,
  description: FAIR_SEO_DEFAULT_DESCRIPTION,
  path: '/',
  ogImage: fairOgImageUrl(),
  jsonLdWebsite: true,
}

/** Route-name → SEO payload. Unknown routes fall back to site defaults. */
const ROUTE_SEO_BY_NAME: Record<string, FairRouteSeo> = {
  home: HOME_SEO,
}

export function getFairRouteSeo(
  routeName: string | symbol | null | undefined,
  path = '/',
): FairRouteSeo {
  const key = typeof routeName === 'string' ? routeName : ''
  const known = ROUTE_SEO_BY_NAME[key]
  if (known) {
    return known
  }
  return {
    title: FAIR_SEO_DEFAULT_TITLE,
    description: FAIR_SEO_DEFAULT_DESCRIPTION,
    path,
    ogImage: fairOgImageUrl(),
  }
}

export function buildFairWebsiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${fairAbsoluteUrl('/')}/#organization`,
        name: 'FAIR',
        url: fairAbsoluteUrl('/'),
        logo: fairAbsoluteUrl('/img/logo.svg'),
      },
      {
        '@type': 'WebSite',
        '@id': `${fairAbsoluteUrl('/')}/#website`,
        name: 'FAIR',
        url: fairAbsoluteUrl('/'),
        publisher: { '@id': `${fairAbsoluteUrl('/')}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${fairAbsoluteUrl('/searchList')}/{search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  }
}
